import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { CalendarRange, ChevronLeft, ChevronRight, RefreshCw, Undo2, ClipboardList } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { formatDate } from '@shared/utils';
import ServiceCallModal from '../components/ServiceCallModal';
import DailySuppliesModal from '../components/DailySuppliesModal';
import { CallCard } from '../components/AssignmentListItems';

const DAYS_IN_VIEW = 7;
const CLOSED_STATUSES = new Set(['cancelled']);
const REFRESH_INTERVAL_MS = 60000;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (date, amount) => {
  const d = new Date(date);
  d.setDate(d.getDate() + amount);
  return d;
};

export default function CalendarPage() {
  const queryClient = useQueryClient();
  const [rangeStart, setRangeStart] = useState(() => startOfDay(new Date()));
  const [selectedCall, setSelectedCall] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [suppliesDate, setSuppliesDate] = useState(null);

  const rangeEnd = useMemo(() => addDays(rangeStart, DAYS_IN_VIEW - 1), [rangeStart]);
  const startStr = useMemo(() => toYmd(rangeStart), [rangeStart]);
  const endStr = useMemo(() => toYmd(rangeEnd), [rangeEnd]);
  const today = useMemo(() => startOfDay(new Date()), []);
  const isCurrentWeek = startStr === toYmd(today);

  const calendarQuery = useQuery(
    ['calendarAssignments', startStr, endStr],
    () => serviceCallsAPI.getCalendarServiceCalls({ start: startStr, end: endStr }),
    { refetchInterval: REFRESH_INTERVAL_MS, refetchOnWindowFocus: true }
  );

  const callsByDay = useMemo(() => {
    const calls = calendarQuery.data?.data?.serviceCalls || [];
    const buckets = new Map();
    for (let i = 0; i < DAYS_IN_VIEW; i += 1) {
      buckets.set(toYmd(addDays(rangeStart, i)), []);
    }
    calls
      .filter((call) => !CLOSED_STATUSES.has(call.status))
      .forEach((call) => {
        if (!call.scheduledDate) return;
        const key = toYmd(new Date(call.scheduledDate));
        if (buckets.has(key)) {
          buckets.get(key).push(call);
        }
      });
    return buckets;
  }, [calendarQuery.data, rangeStart]);

  const totalCalls = useMemo(
    () => Array.from(callsByDay.values()).reduce((sum, list) => sum + list.length, 0),
    [callsByDay]
  );

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries('calendarAssignments');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedCall(null);
    refresh();
  };

  const rangeLabel = `${formatDate(rangeStart, 'MMM d')} – ${formatDate(rangeEnd, 'MMM d')}`;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarRange className="h-6 w-6 text-primary-600" />
            <h1 className="text-2xl font-bold text-slate-900">Calendar</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {rangeLabel}
            {!calendarQuery.isLoading && (
              <span className="ml-2 text-slate-400">
                · {totalCalls} {totalCalls === 1 ? 'call' : 'calls'}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={calendarQuery.isLoading || isRefreshing}
          className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setRangeStart((prev) => addDays(prev, -DAYS_IN_VIEW))}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </button>
        {!isCurrentWeek && (
          <button
            type="button"
            onClick={() => setRangeStart(startOfDay(new Date()))}
            className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 shadow-sm transition-colors hover:bg-primary-100"
          >
            <Undo2 className="h-4 w-4" />
            This week
          </button>
        )}
        <button
          type="button"
          onClick={() => setRangeStart((prev) => addDays(prev, DAYS_IN_VIEW))}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {calendarQuery.isLoading ? (
        <p className="text-sm text-slate-400">Loading your calendar…</p>
      ) : (
        <div className="space-y-5">
          {Array.from({ length: DAYS_IN_VIEW }, (_, i) => {
            const day = addDays(rangeStart, i);
            const key = toYmd(day);
            const calls = callsByDay.get(key) || [];
            const isToday = key === toYmd(today);

            return (
              <DaySection
                key={key}
                day={day}
                dayKey={key}
                isToday={isToday}
                calls={calls}
                onSelectCall={setSelectedCall}
                onOpenPartsList={setSuppliesDate}
              />
            );
          })}
        </div>
      )}

      {selectedCall && (
        <ServiceCallModal
          serviceCall={selectedCall}
          isOpen={!!selectedCall}
          onClose={handleCloseModal}
          isRepairCall={!!selectedCall.isPriority}
        />
      )}

      <DailySuppliesModal
        date={suppliesDate}
        isOpen={!!suppliesDate}
        onClose={() => setSuppliesDate(null)}
      />
    </div>
  );
}

function DaySection({ day, dayKey, isToday, calls, onSelectCall, onOpenPartsList }) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <div
          className={`flex h-9 w-9 flex-shrink-0 flex-col items-center justify-center rounded-xl text-xs font-bold ${
            isToday ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-500'
          }`}
        >
          <span>{formatDate(day, 'd')}</span>
        </div>
        <div>
          <p className={`text-sm font-semibold ${isToday ? 'text-primary-700' : 'text-slate-700'}`}>
            {formatDate(day, 'EEEE')}
            {isToday && <span className="ml-1.5 text-xs font-medium text-primary-500">Today</span>}
          </p>
          <p className="text-xs text-slate-400">{formatDate(day, 'MMM d, yyyy')}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {calls.length > 0 && (
            <button
              type="button"
              onClick={() => onOpenPartsList(dayKey)}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
            >
              <ClipboardList className="h-3.5 w-3.5" />
              Parts List
            </button>
          )}
          <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
            {calls.length}
          </span>
        </div>
      </div>
      {calls.length > 0 ? (
        <div className="space-y-2 pl-1">
          {calls.map((call) => (
            <CallCard
              key={call.id}
              call={call}
              muted={call.status === 'completed'}
              onClick={() => onSelectCall(call)}
            />
          ))}
        </div>
      ) : (
        <p className="pl-1 text-sm text-slate-400">No calls scheduled.</p>
      )}
    </section>
  );
}
